import classNames from 'classnames'
import React, { createContext, MouseEventHandler, useContext, useEffect, useRef, useState } from 'react'

type TriggerProps = {
   componentTag?: string
   className?: string
   children?: React.ReactNode
}

type OverlayProps = {
   className?: string
   children?: React.ReactNode
   style?: React.CSSProperties
}

type Props = {
   children?: React.ReactNode
   onHide?: Function
   onShow?: Function
   onToggle: Function
   isOpen: boolean
   forcedDirection?: 'top' | 'bottom' | null
   keepOpen?: boolean
}

type Context = {
   isOpen: boolean
   trigger: any
   overlay: any
   toggleOverlay: MouseEventHandler<any>
   direction: 'top' | 'bottom' | null
}

const OverlayTriggerContext = createContext({} as Context)

function useOverlayContext() {
   const context = useContext(OverlayTriggerContext)
   if (!context) {
      throw new Error(`Overlay compound components cannot be rendered outside the OverlayTrigger component`)
   }
   return context
}

const Overlay = ({ className, children, style }: OverlayProps) => {
   const { isOpen, overlay, trigger, direction } = useOverlayContext()

   return (
      <div
         ref={overlay}
         className={classNames(className, {
            'itm-overlay-position--up': direction === 'top',
            'itm-overlay-position--bottom': direction === 'bottom',
         })}
         style={{
            ...style,
            ...(!isOpen && {
               display: 'none',
            }),
            ...(!direction && {
               visibility: 'hidden',
            }),
            ...(direction === 'top' &&
               trigger.current && {
                  bottom: trigger.current.offsetHeight + 'px',
               }),
         }}
      >
         {children}
      </div>
   )
}

const Trigger = ({ componentTag = 'span', className, children }: TriggerProps) => {
   const { trigger, toggleOverlay } = useOverlayContext()
   return React.createElement(
      componentTag,
      { ref: trigger, className, ...(toggleOverlay && { onClick: toggleOverlay }) },
      children,
   )
}

const OverlayTrigger = ({ children, onToggle, isOpen, forcedDirection, keepOpen }: Props) => {
   const trigger = useRef<HTMLSpanElement>(null)
   const overlay = useRef<HTMLDivElement>(null)
   // const [scrollPosition, setScrollPosition] = useState(0)
   const [direction, setDirection] = useState<'top' | 'bottom' | null>(forcedDirection ?? null) // This can be used to determine the position of the overlay
   // const [isOpen, _setIsOpen] = useState(isOpen2)
   const isOpenRef = React.useRef(isOpen)

   const setIsOpen = (data: boolean) => {
      isOpenRef.current = data
      onToggle(data)
   }

   useEffect(() => {
      const onScrollEvent = () => {
         if (isOpen) {
            calculateDirection()
         }
      }
      window && window.addEventListener('scrollend', onScrollEvent)
      if (isOpen) {
         window && window.addEventListener('click', onOverlayClose)
      } else {
         window && window.removeEventListener('click', onOverlayClose)
      }
      return () => {
         window && window.removeEventListener('click', onOverlayClose)
         window && window.removeEventListener('scrollend', onScrollEvent)
      }
   }, [isOpen])

   const calculateDirection = () => {
      if (!forcedDirection && overlay.current && trigger.current) {
         const triggerRect = trigger.current.getBoundingClientRect()
         const overlayRect = overlay.current.getBoundingClientRect()
         const viewportHeight = window.innerHeight || document.documentElement.clientHeight
         const spaceBelow = viewportHeight - triggerRect.bottom
         if (spaceBelow < overlayRect.height && triggerRect.top >= overlayRect.height) {
            setDirection('top')
         } else {
            setDirection('bottom')
         }
      }
   }

   function onOverlayClose(e: any) {
      const isTriggerDescendant = trigger && trigger.current?.contains(e.target)
      const isOverlayDescendant = overlay && overlay.current?.contains(e.target)
      if (isOpenRef.current && !isTriggerDescendant && !isOverlayDescendant && !keepOpen) {
         setIsOpen(false)
         if (!forcedDirection) {
            setDirection(null)
         }
      }
   }

   function toggleOverlay(e: any) {
      const isOverlayDescendant = overlay && overlay.current?.contains(e.target)
      const isTriggerDescendant = trigger && trigger.current?.contains(e.target)

      if (!isOverlayDescendant) {
         if (isOpen) {
            setIsOpen(false)
            if (!forcedDirection) {
               setDirection(null)
            }
            // onHide && onHide(isTriggerDescendant, isOverlayDescendant)
         } else {
            setIsOpen(true)
            setTimeout(() => {
               calculateDirection()
            }, 0) // Delay to ensure the overlay is rendered before calculating position
            // onShow && onShow()
         }
      }
   }

   const contextValue = {
      isOpen,
      trigger,
      overlay,
      toggleOverlay,
      direction,
   }

   return <OverlayTriggerContext.Provider value={contextValue}>{children}</OverlayTriggerContext.Provider>
}

OverlayTrigger.Overlay = Overlay
OverlayTrigger.Trigger = Trigger

export { OverlayTrigger }
